// Get current hostname for environment detection
const getCurrentEnvironment = () => {
  const hostname = window.location.hostname;
  
  // For local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'beta';
  }
  
  // For production environments
  if (hostname.includes('beta.hest.app')) {
    return 'beta';
  } else if (hostname.includes('hest.app')) {
    return 'production';
  }
  
  // Default to beta for unknown environments
  return 'beta';
};

export const ENVIRONMENT = getCurrentEnvironment();

// ─── Multi-region routing ──────────────────────────────────────────────────────
// The platform runs one deployment per region. A customer picks a region at signup
// (Canada → ca-central-1); we persist it so every API call targets that region's
// deployment. PLACEHOLDER domains for the ECS→Lambda switch — keep in sync with the
// backend (lib/regions.py) and /api/v1/config/regions.
const REGION_STORAGE_KEY = 'hestia_region';

// Serverless (Lambda + API Gateway) backend — the prod-Hestia HestiaServerlessStack.
// ECS owns api.hest.app, but during the migration the frontend targets the API Gateway
// stage directly. The stage is `/prod`; the app's routes live under `/api/v1`, so the
// base is `<invoke-url>/api/v1`. Override per-deployment with VITE_API_BASE_URL.
const SERVERLESS_API_BASE = 'https://a30rux62e1.execute-api.us-east-1.amazonaws.com/prod/api/v1';

export const REGIONS = [
  { region: 'us-east-1', label: 'United States', apiBaseUrl: SERVERLESS_API_BASE },
  { region: 'ca-central-1', label: 'Canada', apiBaseUrl: 'https://api.ca.hest.app/api/v1' },
];

const REGION_API_BASE = Object.fromEntries(REGIONS.map((r) => [r.region, r.apiBaseUrl]));

// Country is the primary signup input; region is derived from it (infra detail stays internal).
export const COUNTRIES = [
  { code: 'US', label: 'United States', region: 'us-east-1' },
  { code: 'CA', label: 'Canada', region: 'ca-central-1' },
];

export const regionForCountry = (code: string): string => {
  const match = COUNTRIES.find((c) => c.code === code);
  return match ? match.region : 'us-east-1';
};

export const getSelectedRegion = () => {
  try {
    return localStorage.getItem(REGION_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const setSelectedRegion = (region: string | null): void => {
  try {
    if (region) localStorage.setItem(REGION_STORAGE_KEY, region);
    else localStorage.removeItem(REGION_STORAGE_KEY);
  } catch {
    /* ignore storage errors */
  }
};

// API Configuration — Hestia backend API endpoints (region-aware).
export const getApiBaseUrl = () => {
  // Explicit override wins everywhere — set VITE_API_BASE_URL to repoint the API
  // (e.g. a different API Gateway stage) without a code change.
  const override = import.meta.env?.VITE_API_BASE_URL;
  if (override) return override;

  const hostname = window.location.hostname;

  // For local development (single local backend; region split is deployed-only).
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const envLocalApiUrl = import.meta.env?.VITE_LOCAL_API_URL;
    if (envLocalApiUrl) return `${envLocalApiUrl}/hestia`;
    return 'http://localhost:8000/api/v1';
  }

  // A region chosen at signup wins in deployed environments.
  const region = getSelectedRegion();
  if (region && REGION_API_BASE[region]) return REGION_API_BASE[region];

  if (hostname.includes('beta.')) {
    return 'https://api.beta.hest.app/api/v1';
  }
  // Prod default → serverless API Gateway (ECS api.hest.app retired for the frontend
  // during the migration).
  return SERVERLESS_API_BASE;
};

// Evaluated once for back-compat imports; prefer getApiBaseUrl() at call time so a
// region selected after load is honored within the session.
export const API_BASE_URL = getApiBaseUrl();

// React Query Configuration
export const CACHE_TIME = 30 * 60 * 1000; // 30 minutes
export const STALE_TIME = 5 * 60 * 1000;  // 5 minutes
export const MAX_RETRIES = 3;

// Common query options
export const defaultQueryOptions = {
  staleTime: STALE_TIME,
  gcTime: CACHE_TIME,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  retry: (failureCount: number, error: { status?: number } | null) => {
    return failureCount < MAX_RETRIES && error?.status !== 401;
  },
};

// Common mutation options
export const defaultMutationOptions = {
  retry: (failureCount: number, error: { status?: number } | null) => {
    return failureCount < MAX_RETRIES && error?.status !== 401;
  },
};
